import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";

import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Episode,
  Paragraph,
  UpdateSubtitlePayload,
} from "../../../services/episode.service";
import VerticalDotsMenu from "./VerticalDotsMenu";
import { episodeService } from "../../../services";
import { toHHMMSS } from "../../../../../utils/formatTIme";

interface Props {
  episode: Episode;
  currentTime: number;
  onParagraphClick: (paragraph: Paragraph) => void;
  onVideoPlay?: () => void;
  onVideoPause?: () => void;
}

const BOUNDARY_TIME_EPS = 1e-4;

/** When the next segment starts where this one ends, use [start, end) so only one row is active at the boundary. */
function paragraphContainsTime(
  paragraph: Paragraph,
  index: number,
  paragraphs: Paragraph[],
  t: number,
): boolean {
  if (t < paragraph.start) return false;
  const next = paragraphs[index + 1];
  if (
    next != null &&
    Math.abs(next.start - paragraph.end) < BOUNDARY_TIME_EPS
  ) {
    return t < paragraph.end;
  }
  return t <= paragraph.end;
}

// Memoized paragraph component for better performance
const ParagraphItem = React.memo(
  ({
    paragraph,
    index,
    isEditMode,
    currentTime,
    onParagraphClick,
    onTextChange,
    setRef,
    isEditing,
    onStartEdit,
    onStopEdit,
    paragraphs,
  }: {
    paragraph: Paragraph;
    index: number;
    paragraphs: Paragraph[];
    isEditMode: boolean;
    currentTime: number;
    onParagraphClick: (paragraph: Paragraph) => void;
    onTextChange: (index: number, text: string) => void;
    setRef: (index: number, ref: any) => void;
    isEditing: boolean;
    onStartEdit: (index: number) => void;
    onStopEdit: () => void;
  }) => {
    const isCurrentParagraph = useMemo(
      () => paragraphContainsTime(paragraph, index, paragraphs, currentTime),
      [paragraph.start, paragraph.end, index, paragraphs, currentTime],
    );

    const handleClick = useCallback(() => {
      if (isEditMode) {
        onStartEdit(index);
      } else {
        onParagraphClick(paragraph);
      }
    }, [isEditMode, onParagraphClick, paragraph, onStartEdit, index]);

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onTextChange(index, e.target.value);
      },
      [onTextChange, index],
    );

    const handleBlur = useCallback(() => {
      onStopEdit();
    }, [onStopEdit]);

    const setParagraphRef = useCallback(
      (ref: any) => setRef(index, ref),
      [setRef, index],
    );

    // Memoize the background color calculation
    const backgroundColor = useMemo(() => {
      if (isEditMode) {
        return isCurrentParagraph ? "rgba(37, 99, 235, 0.08)" : "transparent";
      }
      return isCurrentParagraph ? "#2563eb" : "transparent";
    }, [isEditMode, isCurrentParagraph]);

    // Memoize the text color calculation
    const textColor = useMemo(() => {
      if (isEditMode) {
        return "#000";
      }
      return isCurrentParagraph ? "#fff" : "inherit";
    }, [isEditMode, isCurrentParagraph]);

    // Memoize the border radius calculation
    const borderRadius = useMemo(
      () => (isCurrentParagraph ? "8px" : "6px"),
      [isCurrentParagraph],
    );

    // Memoize the border calculation for edit mode
    const border = useMemo(() => {
      if (isEditMode && isCurrentParagraph) {
        return "1px solid rgba(37, 99, 235, 0.3)";
      }
      return "none";
    }, [isEditMode, isCurrentParagraph]);

    return (
      <Box
        id={`paragraph-${index}`}
        ref={setParagraphRef}
        onClick={handleClick}
        sx={{
          p: 1.25,
          mb: 0.5,
          backgroundColor,
          color: textColor,
          borderRadius,
          border,
          minHeight: "52px",
          transition: "all 0.15s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: isCurrentParagraph
              ? "#2563eb"
              : "rgba(37, 99, 235, 0.06)",
          },
        }}
      >
        {isEditMode && isEditing ? (
          <TextField
            fullWidth
            multiline
            variant="standard"
            value={paragraph.text || ""}
            onChange={handleTextChange}
            onBlur={handleBlur}
            autoFocus
            sx={{
              "& .MuiInputBase-root": {
                color: "#000",
                minHeight: "1.5em",
                padding: 0,
                lineHeight: "1.5",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                lineHeight: "1.5",
                padding: 0,
                minHeight: "1.5em",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "pre-wrap",
              },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "1.5",
              minHeight: "1.5em",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {paragraph.text || ""}
          </Typography>
        )}
        <Box display="flex" alignItems="center" mt={0.5}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "11px",
              color: isCurrentParagraph
                ? "rgba(255,255,255,0.75)"
                : "text.secondary",
              letterSpacing: "0.02em",
            }}
          >
            {toHHMMSS(paragraph.start.toString())} —{" "}
            {toHHMMSS(paragraph.end.toString())}
          </Typography>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if essential props changed
    return (
      prevProps.isEditMode === nextProps.isEditMode &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.paragraph.text === nextProps.paragraph.text &&
      prevProps.paragraph.start === nextProps.paragraph.start &&
      prevProps.paragraph.end === nextProps.paragraph.end &&
      prevProps.currentTime === nextProps.currentTime &&
      prevProps.paragraphs === nextProps.paragraphs &&
      prevProps.index === nextProps.index
    );
  },
);

ParagraphItem.displayName = "ParagraphItem";

export default function SubtitleEditor({
  episode,
  currentTime,
  onParagraphClick,
  onVideoPlay,
  onVideoPause,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const paragraphRefs = useRef<any[]>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isUserScrolling = useRef(false);
  const lastAutoScrollTime = useRef(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedParagraphs, setEditedParagraphs] = useState<Paragraph[]>([]);
  const [editingParagraphIndex, setEditingParagraphIndex] = useState<
    number | null
  >(null);

  // Memoized calculations
  const currentParagraphs = useMemo(
    () =>
      editedParagraphs.length > 0
        ? editedParagraphs
        : (episode?.paragraphs ?? []),
    [editedParagraphs, episode?.paragraphs],
  );

  const currentParagraphIndex = useMemo(
    () =>
      currentParagraphs.findIndex((paragraph, index) =>
        paragraphContainsTime(paragraph, index, currentParagraphs, currentTime),
      ),
    [currentParagraphs, currentTime],
  );

  const setParagraphRef = useCallback((index: number, ref: any) => {
    paragraphRefs.current[index] = ref;
  }, []);

  // Mutations
  const { mutateAsync: exportSubtitle } = useMutation({
    mutationKey: ["exportSubtitle"],
    mutationFn: (format: string) =>
      episodeService.exportSubtitlesByEpisodeId(episode.id, format),
  });

  const { mutateAsync: updateTranscription } = useMutation({
    mutationKey: ["updateTranscription"],
    mutationFn: (payload: UpdateSubtitlePayload) =>
      episodeService.updateTranscription(episode.id, payload),
  });

  // Memoized callbacks
  const handleSaveChanges = useCallback(
    async (paragraph: Paragraph) => {
      try {
        const isSuccess = await updateTranscription({
          paragraph: {
            id: paragraph.id,
            text: paragraph.text,
          },
        });

        if (isSuccess) {
          toast.success("Transcription updated successfully", {
            autoClose: 1000,
          });
        } else {
          toast.error("Failed to update transcription", {
            autoClose: 1000,
          });
        }
      } catch (error) {
        console.error("Error saving changes:", error);
        toast.error("Failed to update transcription", {
          autoClose: 1000,
        });
      }
    },
    [updateTranscription],
  );

  const handleParagraphTextChange = useCallback(
    (index: number, newText: string) => {
      try {
        if (index < 0 || index >= editedParagraphs.length) return;

        const updatedParagraphs = [...editedParagraphs];
        if (updatedParagraphs[index]) {
          updatedParagraphs[index] = {
            ...updatedParagraphs[index],
            text: newText || "",
          };
          setEditedParagraphs(updatedParagraphs);

          // Trigger debounced save with the modified paragraph
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          saveTimeoutRef.current = setTimeout(() => {
            handleSaveChanges(updatedParagraphs[index]);
          }, 800);
        }
      } catch (error) {
        console.error("Error updating paragraph text:", error);
      }
    },
    [editedParagraphs, handleSaveChanges],
  );

  // Memoized handlers
  const handleExportSubtitle = useCallback(
    async (format: string) => {
      try {
        const response: any = await exportSubtitle(format);
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = `episode-${episode?.name}.${format}`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error(`Error exporting ${format}:`, error);
      }
    },
    [exportSubtitle, episode?.name],
  );

  // Auto-scroll within the transcript panel. Do not use offsetTop — it is relative
  // to offsetParent, not the scroll container, so nested layout / styling breaks it.
  const scrollToParagraph = useCallback((paragraphIndex: number) => {
    const container = containerRef.current;
    if (!container) return;

    const element =
      paragraphRefs.current[paragraphIndex] ??
      document.getElementById(`paragraph-${paragraphIndex}`);
    if (!element) return;

    const elRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const relativeTop = elRect.top - containerRect.top + container.scrollTop;
    const targetScrollTop =
      relativeTop - container.clientHeight / 2 + elRect.height / 2;

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  }, []);

  // Debounced scroll function
  const debouncedScrollToParagraph = useCallback(
    (paragraphIndex: number) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollToParagraph(paragraphIndex);
      }, 50); // Reduced from 100ms to 50ms for faster response
    },
    [scrollToParagraph],
  );

  // Clean edit mode entry
  const handleEditSubtitle = useCallback(() => {
    // Initialize editedParagraphs if needed
    if (editedParagraphs.length === 0) {
      setEditedParagraphs([...(episode?.paragraphs ?? [])]);
    }

    // Enter edit mode
    setIsEditMode(true);
    setEditingParagraphIndex(null);

    // Pause video
    if (onVideoPause) {
      onVideoPause();
    }

    // Clear any pending save timeouts
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Scroll to current paragraph after a short delay
    if (currentParagraphIndex !== -1) {
      setTimeout(() => {
        scrollToParagraph(currentParagraphIndex);
      }, 50); // Reduced from 100ms to 50ms for faster response
    }
  }, [
    editedParagraphs.length,
    episode?.paragraphs,
    onVideoPause,
    currentParagraphIndex,
    scrollToParagraph,
  ]);

  // Clean edit mode exit
  const handleCancelEdit = useCallback(() => {
    // Exit edit mode
    setIsEditMode(false);
    setEditingParagraphIndex(null);

    // Clear any pending save timeouts
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Resume video
    if (onVideoPlay) {
      onVideoPlay();
    }

    // Reset auto-scroll timing
    lastAutoScrollTime.current = 0;

    // Scroll to current paragraph
    if (currentParagraphIndex !== -1) {
      setTimeout(() => {
        scrollToParagraph(currentParagraphIndex);
      }, 50); // Reduced from 100ms to 50ms for faster response
    }
  }, [onVideoPlay, currentParagraphIndex, scrollToParagraph]);

  // Effects
  useEffect(() => {
    if (isEditMode) {
      paragraphRefs.current = [];
    }
  }, [isEditMode]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      if (!currentParagraphs.length) return;

      isUserScrolling.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrolling.current = false;
      }, 1500); // Reduced from 2000ms to 1500ms for faster response
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [currentParagraphs.length]);

  useEffect(() => {
    // Initialize editedParagraphs when episode data changes
    if (episode?.paragraphs && episode.paragraphs.length > 0) {
      const needsUpdate =
        editedParagraphs.length === 0 ||
        editedParagraphs.some((p, i) => p.id !== episode.paragraphs?.[i]?.id);

      if (needsUpdate) {
        setEditedParagraphs([...episode.paragraphs]);
      }
    }
  }, [episode?.paragraphs, editedParagraphs.length]);

  // Auto-scroll effect - clean and reliable
  useEffect(() => {
    if (isEditMode || isUserScrolling.current) return;

    const now = Date.now();
    if (now - lastAutoScrollTime.current < 200) {
      // Reduced from 300ms to 200ms for faster response
      return;
    }
    lastAutoScrollTime.current = now;

    if (currentParagraphIndex === -1) return;

    debouncedScrollToParagraph(currentParagraphIndex);
  }, [
    currentTime,
    currentParagraphIndex,
    isEditMode,
    debouncedScrollToParagraph,
  ]);

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          pb: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 0.5,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}
          >
            Transcript
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0 }}>
            {currentParagraphs.length} segments
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Tooltip title="Copy transcript">
            <IconButton
              size="small"
              onClick={() => {
                const text = currentParagraphs.map((p) => p.text).join("\n\n");
                navigator.clipboard.writeText(text);
              }}
              sx={{ color: "text.secondary", "&:hover": { color: "#2563eb" } }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <VerticalDotsMenu
            onExportSubtitle={handleExportSubtitle}
            onEditSubtitle={handleEditSubtitle}
          />
        </Box>
      </Box>

      {isEditMode && (
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexShrink: 0 }}>
          <Button variant="outlined" onClick={handleCancelEdit} size="small">
            Exit Edit Mode
          </Button>
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          pr: 1,
          overflowY: "auto",
          overflowX: "hidden",
          mt: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {currentParagraphs.map((paragraph, index) => (
          <ParagraphItem
            key={paragraph.id}
            paragraph={paragraph}
            index={index}
            paragraphs={currentParagraphs}
            isEditMode={isEditMode}
            currentTime={currentTime}
            onParagraphClick={onParagraphClick}
            onTextChange={handleParagraphTextChange}
            setRef={setParagraphRef}
            isEditing={editingParagraphIndex === index}
            onStartEdit={setEditingParagraphIndex}
            onStopEdit={() => setEditingParagraphIndex(null)}
          />
        ))}
      </Box>
    </Box>
  );
}
